'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Phone, User, Building2, GitBranch, FileText, Package, CheckCircle2 } from 'lucide-react';
import SignupFormView from './SignupFormView';

type SignupStep = 'phone-verify' | 'account' | 'gym' | 'branch' | 'documents' | 'plan' | 'confirm';

interface SignupValues {
  fullName: string;
  phone: string;
  gymName: string;
  gymType: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gymPhone: string;
  gymEmail: string;
  branchName: string;
  branchAddress: string;
  branchCity: string;
  branchPhone: string;
  branchLat: string;
  branchLng: string;
  geofenceRadius: string;
}

interface UploadedDoc {
  name: string;
  type: string;
  url: string;
  size: number;
  _file?: File;
}

export interface DbPlan {
  id: string;
  name: string;
  slug: string;
  price: number;
  yearly_price: number;
  billing_period: string;
  billing_interval: string;
  member_limit: number;
  max_staff: number | null;
  max_branches: number | null;
  max_trainers: number | null;
  trial_days: number | null;
  discount: number | null;
  yearly_discount_percent: number | null;
  is_popular: boolean;
  promo_text: string | null;
  description: string | null;
  features: string[] | null;
  feature_keys: string[] | null;
  is_active: boolean;
  sort_order: number;
}

const STEP_ORDER: SignupStep[] = ['phone-verify', 'account', 'gym', 'branch', 'documents', 'plan', 'confirm'];

const DOC_TYPES = [
  { value: 'business_registration', label: 'Business Registration' },
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'id_proof', label: 'Owner ID Proof' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'other', label: 'Other Document' },
];

export default function SignupForm({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<SignupStep>('phone-verify');

  // Phone verification state
  const [verifyPhone, setVerifyPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // PIN state
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);
  const confirmPinRef = useRef<HTMLInputElement>(null);

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('growth');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [uploading, setUploading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  // DB-driven plans state
  const [dbPlans, setDbPlans] = useState<DbPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // "Same as above" toggles
  const [sameGymPhone, setSameGymPhone] = useState(false);
  const [sameBranchAddress, setSameBranchAddress] = useState(false);
  const [sameBranchCity, setSameBranchCity] = useState(false);
  const [sameBranchPhone, setSameBranchPhone] = useState(false);

  // Track which steps have been visited (for free navigation)
  const [visitedSteps, setVisitedSteps] = useState<Set<SignupStep>>(new Set(['phone-verify']));

  const { register, handleSubmit, formState: { errors }, watch, trigger, setValue, getValues } = useForm<SignupValues>({
    defaultValues: {
      branchName: 'Main Branch',
      geofenceRadius: '100',
      gymType: 'fitness',
    }
  });

  const watchedValues = watch();

  // Fetch plans from DB on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch('/api/plans');
        const data = await res.json();
        if (data.plans && data.plans.length > 0) {
          setDbPlans(data.plans);
          // Default to the popular plan if available
          const popularPlan = data.plans.find((p: DbPlan) => p.is_popular);
          if (popularPlan) setSelectedPlan(popularPlan.slug);
          else setSelectedPlan(data.plans[0].slug);
        }
      } catch {
        // Plans will use fallback from API
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Start resend cooldown timer
  const startResendCooldown = useCallback((seconds = 60) => {
    setResendCooldown(seconds);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Send OTP
  const handleSendOtp = async () => {
    const phoneVal = verifyPhone.replace(/\D/g, '');
    if (!/^[6-9]\d{9}$/.test(phoneVal)) {
      setOtpError('Enter a valid 10-digit Indian mobile number.');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      const res = await fetch('/api/auth/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneVal }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.error || 'Failed to send OTP. Please try again.');
        return;
      }
      setOtpSent(true);
      setDevOtp(data.dev_otp || '');
      startResendCooldown(60);
      setTimeout(() => otpInputRef.current?.focus(), 100);
      toast.success('OTP sent!');
    } catch {
      setOtpError('Network error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    setOtpError('');
    if (otp.length !== 6) {
      setOtpError('Enter the 6-digit OTP.');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch('/api/auth/otp/send', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: verifyPhone.replace(/\D/g, ''), otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOtpError(data.error || 'Incorrect OTP. Please try again.');
        return;
      }
      setOtpVerified(true);
      setValue('phone', verifyPhone.replace(/\D/g, ''));
      toast.success('Phone number verified!');
      setVisitedSteps(prev => new Set([...prev, 'account']));
      setStep('account');
      setTimeout(() => pinRef.current?.focus(), 100);
    } catch {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setOtp('');
    setOtpError('');
    await handleSendOtp();
  };

  // Navigate to a step directly (only if visited or next step)
  const navigateToStep = (targetStep: SignupStep) => {
    const targetIdx = STEP_ORDER.indexOf(targetStep);
    const currentIdx = STEP_ORDER.indexOf(step);
    if (visitedSteps.has(targetStep) || targetIdx <= currentIdx + 1) {
      setStep(targetStep);
    }
  };

  // "Same as above" handlers
  const handleSameGymPhone = (checked: boolean) => {
    setSameGymPhone(checked);
    if (checked) setValue('gymPhone', verifyPhone.replace(/\D/g, ''));
  };

  const handleSameBranchAddress = (checked: boolean) => {
    setSameBranchAddress(checked);
    if (checked) setValue('branchAddress', getValues('address'));
  };

  const handleSameBranchCity = (checked: boolean) => {
    setSameBranchCity(checked);
    if (checked) setValue('branchCity', getValues('city'));
  };

  const handleSameBranchPhone = (checked: boolean) => {
    setSameBranchPhone(checked);
    if (checked) setValue('branchPhone', verifyPhone.replace(/\D/g, ''));
  };

  const handleGetLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setValue('branchLat', pos.coords.latitude.toFixed(6));
        setValue('branchLng', pos.coords.longitude.toFixed(6));
        toast.success('Location captured!');
        setGettingLocation(false);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) toast.error('Location permission denied');
        else toast.error('Could not get location');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [setValue]);

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, PDF, DOC, or DOCX files allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    if (uploadedDocs.length >= 5) {
      toast.error('Maximum 5 documents allowed');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedDocs(prev => [...prev, {
          name: file.name,
          type: 'other',
          url: '',
          size: file.size,
          _file: file,
        }]);
        toast.success(`${file.name} ready to upload`);
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Failed to prepare document');
      setUploading(false);
    } finally {
      e.target.value = '';
    }
  };

  const removeDoc = (idx: number) => {
    setUploadedDocs(prev => prev.filter((_, i) => i !== idx));
  };

  const updateDocType = (idx: number, type: string) => {
    setUploadedDocs(prev => prev.map((d, i) => i === idx ? { ...d, type } : d));
  };

  const nextStep = async () => {
    const currentIdx = STEP_ORDER.indexOf(step);
    const nextStepId = STEP_ORDER[currentIdx + 1];
    if (!nextStepId) return;

    if (step === 'account') {
      const valid = await trigger(['fullName', 'phone']);
      if (!valid) return;
      if (pin.length !== 4) { toast.error('Set your 4-digit PIN'); pinRef.current?.focus(); return; }
      if (pin !== confirmPin) { toast.error('PINs do not match'); confirmPinRef.current?.focus(); return; }
    } else if (step === 'gym') {
      const valid = await trigger(['gymName', 'address', 'city', 'state']);
      if (!valid) return;
    } else if (step === 'branch') {
      const valid = await trigger(['branchName', 'branchAddress', 'branchCity']);
      if (!valid) return;
    }

    setVisitedSteps(prev => new Set([...prev, nextStepId]));
    setStep(nextStepId);
  };

  const prevStep = () => {
    const currentIdx = STEP_ORDER.indexOf(step);
    if (currentIdx > 0) setStep(STEP_ORDER[currentIdx - 1]);
  };

  const onSubmit = async (data: SignupValues) => {
    if (!otpVerified) { toast.error('Please verify your phone number first'); setStep('phone-verify'); return; }
    if (pin.length !== 4) { toast.error('Set a 4-digit PIN'); return; }
    if (pin !== confirmPin) { toast.error('PINs do not match'); return; }
    if (!consent) { toast.error('Please accept Terms & Privacy Policy'); return; }

    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/owner-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: data.phone,
          fullName: data.fullName,
          pin,
          gymName: data.gymName,
          gymType: data.gymType,
          address: data.address,
          city: data.city,
          state: data.state,
          pincode: data.pincode,
          gymPhone: data.gymPhone,
          gymEmail: data.gymEmail,
          branchName: data.branchName || 'Main Branch',
          branchAddress: data.branchAddress,
          branchCity: data.branchCity,
          branchPhone: data.branchPhone,
          branchLat: data.branchLat ? parseFloat(data.branchLat) : null,
          branchLng: data.branchLng ? parseFloat(data.branchLng) : null,
          geofenceRadius: parseInt(data.geofenceRadius || '100'),
          selectedPlan,
          billingInterval: billingCycle,
          uploadedDocs: [],
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.error || 'Registration failed. Please try again.');
        setIsLoading(false);
        return;
      }

      if (result.requires_email_confirmation) {
        toast.success('Account created! Please sign in with your PIN.');
        window.location.href = '/sign-up-login-screen';
        return;
      }

      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      let sessionEstablished = false;

      if (result.session?.access_token && result.session?.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
        if (!sessionError) sessionEstablished = true;
      }

      if (!sessionEstablished && result.user_id) {
        const virtualEmail = `${data.phone}@gymvridhi.internal`;
        const deterministicPassword = `gv_pin_${result.user_id}`;
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: virtualEmail,
          password: deterministicPassword,
        });
        if (!signInError) sessionEstablished = true;
      }

      if (sessionEstablished && result.gym_id && uploadedDocs.length > 0) {
        const docsWithFiles = uploadedDocs as (UploadedDoc & { _file?: File })[];
        const uploadPromises = docsWithFiles.map(async (doc) => {
          if (!doc._file) return null;
          try {
            const formData = new FormData();
            formData.append('file', doc._file);
            formData.append('document_type', doc.type || 'other');
            formData.append('gym_id', result.gym_id);
            const uploadRes = await fetch('/api/documents/upload', { method: 'POST', body: formData });
            return uploadRes.ok ? await uploadRes.json() : null;
          } catch { return null; }
        });
        await Promise.allSettled(uploadPromises);
      }

      toast.success('Gym registered! Welcome to GYM VRIDHI. Your first month is FREE!');
      window.location.href = '/owner-dashboard';

    } catch (err) {
      console.error('Signup error:', err);
      toast.error('Registration failed. Please try again.');
      setIsLoading(false);
    }
  };

  const steps: { id: SignupStep; label: string; icon: React.ElementType }[] = [
    { id: 'phone-verify', label: 'Verify', icon: Phone },
    { id: 'account', label: 'Account', icon: User },
    { id: 'gym', label: 'Gym', icon: Building2 },
    { id: 'branch', label: 'Branch', icon: GitBranch },
    { id: 'documents', label: 'Docs', icon: FileText },
    { id: 'plan', label: 'Plan', icon: Package },
    { id: 'confirm', label: 'Confirm', icon: CheckCircle2 },
  ];

  const currentStepIdx = STEP_ORDER.indexOf(step);
  const selectedPlanData = dbPlans.find(p => p.slug === selectedPlan);
  const activePlanPrice = billingCycle === 'yearly'
    ? (selectedPlanData?.yearly_price ?? 0)
    : (selectedPlanData?.price ?? 0);

  return (
    <SignupFormView
      step={step}
      steps={steps}
      currentStepIdx={currentStepIdx}
      visitedSteps={visitedSteps}
      navigateToStep={navigateToStep}
      onBack={onBack}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      verifyPhone={verifyPhone}
      setVerifyPhone={setVerifyPhone}
      otp={otp}
      setOtp={setOtp}
      otpSent={otpSent}
      otpVerified={otpVerified}
      otpError={otpError}
      setOtpError={setOtpError}
      devOtp={devOtp}
      resendCooldown={resendCooldown}
      otpLoading={otpLoading}
      otpInputRef={otpInputRef}
      handleSendOtp={handleSendOtp}
      handleVerifyOtp={handleVerifyOtp}
      handleResendOtp={handleResendOtp}
      register={register}
      errors={errors}
      watchedValues={watchedValues}
      pin={pin}
      setPin={setPin}
      confirmPin={confirmPin}
      setConfirmPin={setConfirmPin}
      showPin={showPin}
      setShowPin={setShowPin}
      showConfirmPin={showConfirmPin}
      setShowConfirmPin={setShowConfirmPin}
      pinRef={pinRef}
      confirmPinRef={confirmPinRef}
      otpVerifiedPhone={verifyPhone}
      sameGymPhone={sameGymPhone}
      handleSameGymPhone={handleSameGymPhone}
      sameBranchAddress={sameBranchAddress}
      handleSameBranchAddress={handleSameBranchAddress}
      sameBranchCity={sameBranchCity}
      handleSameBranchCity={handleSameBranchCity}
      sameBranchPhone={sameBranchPhone}
      handleSameBranchPhone={handleSameBranchPhone}
      handleGetLocation={handleGetLocation}
      gettingLocation={gettingLocation}
      uploadedDocs={uploadedDocs}
      uploading={uploading}
      handleDocUpload={handleDocUpload}
      removeDoc={removeDoc}
      updateDocType={updateDocType}
      selectedPlan={selectedPlan}
      setSelectedPlan={setSelectedPlan}
      billingCycle={billingCycle}
      setBillingCycle={setBillingCycle}
      selectedPlanData={selectedPlanData}
      activePlanPrice={activePlanPrice}
      dbPlans={dbPlans}
      plansLoading={plansLoading}
      docTypes={DOC_TYPES}
      consent={consent}
      setConsent={setConsent}
      isLoading={isLoading}
      nextStep={nextStep}
      prevStep={prevStep}
    />
  );
}